import { CodeResponse, useGoogleLogin } from "@react-oauth/google";
import React from "react";
import toast from "react-hot-toast";
import { AuthUser, useAuth } from "../../context/AuthContext";
import { trackEvent } from "../../lib/analytics";
import api from "../../lib/api";
import { markSurveyPendingIfNew } from "../../lib/onboardingSurvey";
import { getErrorMessage } from "../../lib/apiError";
import { GoogleButton } from "./GoogleLoginButton.styles";

const GoogleLoginButton: React.FC = () => {
  const { setAuth } = useAuth();

  const onSuccess = (tokenResponse: CodeResponse) => {
    const handleLogin = async () => {
      try {
        // Backend expects a "code" for access token exchange OR idToken path.
        // We'll use the code flow to call POST /v1/google-login-web
        const code = tokenResponse?.code as string;
        if (!code) {
          trackEvent("login_failed", { method: "google", reason: "missing_code" });
          toast.error("Failed to login with Google. Please try again.");
          return;
        }

        const { data } = await api.post<{
          token: string;
          user: AuthUser;
          isNewUser?: boolean;
        }>("/auth/google-login-web", {
          code,
          firstName: "",
          lastName: "",
        });

        setAuth({ token: data.token, user: data.user });
        markSurveyPendingIfNew(data.isNewUser, data.user.id);
        trackEvent("login_success", { method: "google" });
        toast.success("Successfully logged in!");
      } catch (e) {
        console.error("Google login error:", e);
        trackEvent("login_failed", { method: "google", reason: "exception" });
        const errorMessage = getErrorMessage(e, "Failed to login with Google. Please try again.");
        toast.error(errorMessage);
      }
    };

    handleLogin();
  };

  const onError = () => {
    trackEvent("login_failed", { method: "google", reason: "oauth_error" });
    toast.error("Google login was cancelled or failed. Please try again.");
  };

  const login = useGoogleLogin({
    onSuccess,
    onError,
    flow: "auth-code",
    scope: "openid email profile",
  });

  return (
    <GoogleButton onClick={() => login()}>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
        />
        <path
          fill="#FBBC05"
          d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"
        />
      </svg>
      Continue with Google
    </GoogleButton>
  );
};

export default GoogleLoginButton;
